<template>
    <div>
    <v-data-table
        class="mt-5 black--text user-table"
        :class="{ 'channels-table': !!allChannelsCards, 'channels-table--no-cascades': hasAnyCascadesOnProject === false }"
        :headers="headers"
        :items="tableItems"
        :items-per-page="-1"
        hide-default-footer
        @click:row="handleRowClick"
    >
        <!-- Кастомные заголовки с подсказками для колонок приоритетов -->
        <template v-slot:header.mailingsPriority="{ header }">
            <div class="priority-header">
                <span>{{ header.text }}</span>
                <v-tooltip bottom color="#000000DE" max-width="400px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon
                            small
                            class="hint-icon ml-1"
                            v-bind="attrs"
                            v-on="on"
                        >mdi-help-circle</v-icon>
                    </template>
                    <div class="white--text">
                        Укажите порядок каналов (1 → 2 → 3) для Маркетинговых рассылок по базе. Если первый канал недоступен, система использует следующий.<br><br>
                        Сообщение отправляется по каналам по очереди в течение суток, чтобы доставка была аккуратной и не раздражала клиентов.
                    </div>
                </v-tooltip>
            </div>
        </template>
        <template v-slot:header.notificationsPriority="{ header }">
            <div class="priority-header">
                <span>{{ header.text }}</span>
                <v-tooltip bottom color="#000000DE" max-width="400px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon
                            small
                            class="hint-icon ml-1"
                            v-bind="attrs"
                            v-on="on"
                        >mdi-help-circle</v-icon>
                    </template>
                    <div class="white--text">
                        Укажите порядок каналов (1 → 2 → 3) для сервисных уведомлений. Если первый не сработает, используем следующий.<br><br>
                        За один час сообщение отправится по всем каналам по очереди, пока не доставится.
                    </div>
                </v-tooltip>
            </div>
        </template>
        <template v-slot:header.crmMessagesPriority="{ header }">
            <div class="priority-header">
                <span>{{ header.text }}</span>
                <v-tooltip bottom color="#000000DE" max-width="400px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon
                            small
                            class="hint-icon ml-1"
                            v-bind="attrs"
                            v-on="on"
                        >mdi-help-circle</v-icon>
                    </template>
                    <span class="white--text">Установите приоритет отправки для сообщений из Yclients (коды подтверждения)</span>
                </v-tooltip>
            </div>
        </template>
        <template v-slot:[`item.connectorType`]="{ item }">
            <!-- Отдельное оформление для каналов -->
            <div
                v-if="allChannelsCards"
                class="channel-cell"
                :class="{ 'channel-cell--clickable': canOpenChannelSettings(item) || canFinishConnectorCreate(item) }"
            >
                <v-avatar 
                    :color="getConnectorAvatar(item).color" 
                    size="36" 
                    class="mx-2 my-2"
                    :class="{ 'inactive-avatar': item.isActive === false }"
                >
                    <v-img
                        v-if="getConnectorAvatar(item).image"
                        transition="slide-x-transition"
                        :src="getConnectorAvatar(item).image"
                        :class="{ 'inactive-image': item.isActive === false }"
                    />
                    <span v-else>{{ getConnectorAvatar(item).symbol }}</span>
                </v-avatar>
                <div class="channel-text">
                    <div
                        :class="[
                            'channel-name',
                            { 'channel-name--inactive': item.isActive === false }
                        ]"
                    >
                        {{ getConnectorAvatar(item).name }}
                        <span v-if="item.isDraft"> (Draft)</span>
                    </div>
                    <div v-if="item.phone" class="channel-phone">
                        {{ item.phone }}
                    </div>
                </div>
            </div>
            <!-- Стандартное оформление для остальных вкладок -->
            <div v-else class="connector-row">
                <v-avatar 
                    :color="getConnectorAvatar(item).color" 
                    size="36" 
                    class="mx-2 my-2"
                    :class="{ 'inactive-avatar': item.isActive === false }"
                >
                    <v-img
                        v-if="getConnectorAvatar(item).image"
                        transition="slide-x-transition"
                        :src="getConnectorAvatar(item).image"
                        :class="{ 'inactive-image': item.isActive === false }"
                    />
                    <span v-else>{{ getConnectorAvatar(item).symbol }}</span>
                </v-avatar>
                <span class="body-1 connector-row__name">
                    {{ getConnectorAvatar(item).name }}
                    <span v-if="item.phone" class="grey--text text--darken-1">({{ item.phone }})</span>
                    <span v-if="item.isDraft"> (Draft)</span>
                </span>
                <!-- QR-кнопка и иконка статуса для whatsappBasis коннектора (аналогично ConnectorCard) -->
                <v-tooltip v-if="showWhatsappBasisQrcode(item)" bottom color="#000000DE">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            icon
                            medium
                            class="qr-btn"
                            v-bind="attrs"
                            v-on="on"
                            @click.stop="shareWhatsappBasisQrCode(item)"
                        >
                            <v-icon class="qr-icon">mdi-qrcode-scan</v-icon>
                            <v-icon
                                v-if="getWhatsappBasisStatusIcon(item)"
                                :class="['status-icon', getWhatsappBasisStatusIconColor(item)]"
                            >
                                {{ getWhatsappBasisStatusIcon(item) }}
                            </v-icon>
                        </v-btn>
                    </template>
                    <span class="white--text">Поделиться QR-кодом</span>
                </v-tooltip>
            </div>
        </template>

        <!-- Колонка статуса авторизации и кнопок подключения -->
        <template v-slot:[`item.authStatus`]="{ item }">
            <!-- Для активных каналов показываем статус авторизации -->
            <div
                v-if="allChannelsCards && item.isActive !== false && !isHrTelegram(item)"
                class="auth-status-cell"
                @click.stop
            >
                <div
                    :class="[
                        'auth-status-wrapper',
                        { 'auth-status-wrapper--error': !isAuthStatusLoading(item) && !isChannelAuthorized(item) }
                    ]"
                    @click="!isAuthStatusLoading(item) && !isChannelAuthorized(item) && handleAuthStatusClick(item)"
                    :style="{ cursor: !isAuthStatusLoading(item) && !isChannelAuthorized(item) ? 'pointer' : 'default' }"
                >
                    <v-progress-circular
                        v-if="isAuthStatusLoading(item)"
                        indeterminate
                        size="18"
                        width="2"
                        color="mainNew"
                        class="auth-status-spinner"
                    />
                    <img
                        v-else-if="isChannelAuthorized(item)"
                        class="auth-status-icon auth-status-icon--ok"
                        :src="require('@/assets/icons/success-status.svg')"
                    />
                    <img
                        v-else
                        class="auth-status-icon"
                        :src="require('@/assets/icons/error-status.svg')"
                    />
                    <span
                        v-if="isAuthStatusLoading(item)"
                        class="auth-status-text"
                    >
                        Проверка...
                    </span>
                    <span
                        v-else-if="isChannelAuthorized(item)"
                        class="auth-status-text auth-status-text--ok"
                    >
                        Подключен
                        <!-- SMS: привязка и связь — разное. Показываем связь ВСЕГДА,
                             чтобы «Подключен» не читался как «работает». -->
                        <span
                            v-if="smsOnlineLabel(item)"
                            class="auth-status-text"
                            :class="isSmsChannelProblem(item) ? 'auth-status-text--offline' : 'auth-status-text--online'"
                        >
                            · {{ smsOnlineLabel(item) }}
                        </span>
                    </span>
                    <div v-else>
                        <span
                            class="auth-status-text auth-status-text--error"
                        >
                            Не подключен
                        </span>
                    </div>                    
                </div>
                <v-tooltip v-if="!isAuthStatusLoading(item) && !isChannelAuthorized(item) && shouldShowQrCodeButton(item)" bottom color="#000000DE">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            icon
                            small
                            class="qr-btn ml-8"
                            v-bind="attrs"
                            v-on="on"
                            @click.stop="shareWhatsappBasisQrCode(item)"
                        >
                            <v-icon size="22">mdi-qrcode-scan</v-icon>
                        </v-btn>
                    </template>
                    <span class="white--text">Поделиться QR-кодом</span>
                </v-tooltip>
            </div>
            <!-- Для неактивных каналов показываем кнопку подключения -->
            <div
                v-else-if="allChannelsCards && item.isActive === false && checkRole(projectId, 'project:connector:edit')"
                class="auth-status-cell"
                @click.stop
            >
                <v-btn
                    class="connect-btn-list white--text"
                    color="mainNew"
                    @click="connectInactiveChannel(item)"
                    :disabled="!checkRole(projectId, 'project:connector:edit')"
                    small
                >
                    Добавить
                </v-btn>
            </div>
        </template>

        <!-- Колонка приоритета для маркетинговых рассылок -->
        <template v-slot:[`item.mailingsPriority`]="{ item }">
            <div v-if="allChannelsCards && item.isActive !== false && isChannelType(item)" class="priority-cell">
                <PrioritySelect
                    :value="getMailingsPriorityForWhatsappDh(item)"
                    :max-priority="getMaxPriorityForType('mailings')"
                    :excluded-priorities="getExcludedPriorities(item.connectorId || item.id, 'mailings')"
                    :disabled="isWhatsappDhChannel(item) && isMailingsPriorityLocked(item)"
                    :tooltip="getMailingsPriorityTooltip(item)"
                    @input="handleMailingsPriorityInput(item, $event)"
                />
            </div>
        </template>

        <!-- Колонка приоритета для сервисных уведомлений -->
        <template v-slot:[`item.notificationsPriority`]="{ item }">
            <div v-if="allChannelsCards && item.isActive !== false && isChannelType(item)" class="priority-cell">
                <PrioritySelect
                    :value="getNotificationsPriorityForWhatsappDh ? getNotificationsPriorityForWhatsappDh(item) : getChannelPriority(item.connectorId || item.id, 'notifications')"
                    :max-priority="getMaxPriorityForType('notifications')"
                    :excluded-priorities="getExcludedPriorities(item.connectorId || item.id, 'notifications')"
                    :disabled="isWhatsappDhChannel(item)"
                    :tooltip="getNotificationsPriorityTooltip ? getNotificationsPriorityTooltip(item) : ''"
                    @input="updateChannelPriority(item.connectorId || item.id, 'notifications', $event)"
                />
            </div>
        </template>

        <!-- Колонка приоритета для сообщений из CRM -->
        <template v-slot:[`item.crmMessagesPriority`]="{ item }">
            <div v-if="allChannelsCards && item.isActive !== false && isChannelType(item)" class="priority-cell">
                <PrioritySelect
                    :value="getCrmMessagesPriorityForWhatsappDh(item)"
                    :max-priority="getMaxPriorityForType('crmMessages')"
                    :excluded-priorities="getExcludedPriorities(item.connectorId || item.id, 'crmMessages')"
                    :disabled="isWhatsappDhChannel(item)"
                    :tooltip="getCrmMessagesPriorityTooltip(item)"
                    @input="updateChannelPriority(item.connectorId || item.id, 'crmMessages', $event)"
                />
            </div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
            <!-- Для неактивных каналов (не каналы) показываем кнопку подключения -->
            <div v-if="!allChannelsCards && item.isActive === false && checkRole(projectId, 'project:connector:edit')" @click.stop>
                <v-btn
                    class="connect-btn-list white--text"
                    color="mainNew"
                    @click="connectInactiveChannel(item)"
                    :disabled="!checkRole(projectId, 'project:connector:edit')"
                    small
                >
                    Добавить
                </v-btn>
            </div>
            <v-menu v-else-if="!isHrTelegram(item) && item.isActive !== false && !allChannelsCards" :close-on-content-click="false" offset-x offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        medium
                        v-bind="attrs"
                        v-on="on"
                        v-if="checkRole(projectId, 'project:connector:edit')"
                    >
                        <v-icon>mdi-dots-vertical</v-icon></v-btn
                    >
                </template>

                <v-card min-width="198px">
                    <v-list>
                        <v-list-item
                            class="text-btn-wrapper"
                            @click="finishConnectorCreate(item)"
                            v-if="item.isDraft && checkRole(projectId, 'project:connector:edit')"
                        >
                            <span class="mx-3 my-2">Завершить подключение</span>
                        </v-list-item>
                        <v-list-item
                            class="text-btn-wrapper"
                            @click="openConnectorSettings(item)"
                            v-if="!item.isDraft && checkRole(projectId, 'project:connector:edit')"
                        >
                            <span class="mx-3 my-2">Настройки</span>
                        </v-list-item>

                        <v-list-item
                            class="text-btn-wrapper"
                            @click="deleteConnector(item)"
                            v-if="isTester"
                        >
                            <span class="mx-3 my-2">Удалить</span>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-menu>
        </template>
    </v-data-table>
    <MaxMarketingRiskModal
        :value="maxRiskModalOpen"
        :context="maxRiskContext"
        @accept="acceptMaxRisk"
        @remove="removeMaxFromMailings"
        @dismiss="dismissMaxRisk"
    />
    </div>
</template>

<script>
// @ is an alias to /src
import { checkAccess } from '@/utils'
import ConnectorCard from '@/components/project/ConnectorCard'
import PrioritySelect from '@/components/project/PrioritySelect.vue'
import MaxMarketingRiskModal from '@/components/project/MaxMarketingRiskModal.vue'
import { isRegularMaxChannel } from '@/helpers/channels/isRegularMaxChannel'
import { checkRole } from '@root/src/helpers/roles'
import { CONNECTOR_TYPES, PROVIDER_TYPES, TELEPHONY_CONNECTOR_TYPES, CHANNEL_TYPES, PROVIDER_TO_CHANNEL_MAP, GCB_EXTERNAL_CONNECTOR_TYPES } from '@root/src/vars/general'
import cfg from '../../../config'
import api from '@root/src/api/api'
import { isSmsGateStatusAuthorized } from '@/helpers/channels/smsGateStatus'
import { useBillingStore } from '@/store/stores/billing'
import { getChannelPriorities, updateChannelPriority as updateChannelPriorityAPI, fetchConnector } from '@/api/projects'
import {
    getCascadesByProject,
    updateCascadeChannelPriority,
    updateCascadeChannelsPriorities,
    deleteCascadeChannel,
    getCascadeById,
    createChannel,
} from '@/api/networkFixer'
export default {
    name: 'ConnectorsListView',
    components: { ConnectorCard, PrioritySelect, MaxMarketingRiskModal },
    data: () => ({
        selectedConnectors: [],
        whatsappBasisNames: {}, // Кеш для имен whatsappBasis коннекторов
        whatsappBasisConnectorId: null, // ID единственного whatsappBasis коннектора
        whatsappBasisChannelType: null, // Тип канала внутри whatsappBasis (basis-api, wApi-telegram и т.п.)
        whatsappBasisAuthStatus: null, // Статус авторизации whatsappBasis (alive)
        whatsappBasisAuthInterval: null, // Интервал опроса статуса авторизации whatsappBasis
        localChannelPriorities: {},
        maxRiskModalOpen: false,
        maxRiskPending: null,
        maxRiskContext: '',
        savePriorityTimer: null, // Таймер для debounce сохранения приоритетов
        isLoadingPriorities: false,
        cascades: {}, // Кеш каскадов: { mailings: Cascade, notifications: Cascade, crmMessages: Cascade }
        cascadeIds: {}, // ID каскадов по типам: { mailings: number, notifications: number, crmMessages: number }
        hasAnyCascadesOnProject: null, // null = неизвестно, boolean = после загрузки каскадов
        hasLoadedCascades: false,
        // online по lastSeen для sms-gate: список знает только alive (привязку)
        smsOnlineByConnectorId: {},
        // Привязка sms-gate (deviceId в канале) — тот же источник, что у настроек канала.
        smsConnectedByConnectorId: {},
        smsPhoneByConnectorId: {},
        smsOnlineLoadingByConnectorId: {},
        channelAuthStatusByConnectorId: {},
        channelAuthStatusLoadingByConnectorId: {},
    }),
    props: {
        connectors: {
            type: Array,
            default: () => [],
        },
        allChannelsCards: {
            type: Array,
            default: null,
        },
        allTelephonyCards: {
            type: Array,
            default: null,
        },
        allSourcesCards: {
            type: Array,
            default: null,
        },
        channelPriorities: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        hasBotConnector() {
            const connectors = this.project?.connectors || []
            return Boolean(connectors.some((con) => con.connectorType === CONNECTOR_TYPES.whatsappBasis))
        },
        hasYClientsAndBotConnector() {
            const connectors = this.project?.connectors || []
            const hasYClients = connectors.some((con) => con.connectorType === CONNECTOR_TYPES.yClients)
            const hasBot = connectors.some((con) => con.connectorType === CONNECTOR_TYPES.whatsappBasis)
            return Boolean(hasYClients && hasBot)
        },
        headers() {
            // Если передан allChannelsCards, это каналы
            if (this.allChannelsCards) {
                const baseHeaders = [
                    { text: 'Каналы', value: 'connectorType', sortable: false },
                    {
                        text: '',
                        value: 'authStatus',
                        sortable: false,
                        // В режиме без каскадов выравниваем вправо, иначе td получает text-center и всё выглядит "по центру таблицы"
                        align: this.hasAnyCascadesOnProject === false ? 'end' : 'center',
                        minWidth: '140px',
                        width: this.hasAnyCascadesOnProject === false ? '140px' : undefined,
                    },
                ]

                // Если на проекте нет каскадов — не показываем колонки приоритетов и убираем пустую колонку actions,
                // чтобы статусы/кнопки "Добавить" визуально были отцентрированы справа.
                if (!this.hasLoadedCascades || !this.hasAnyCascadesOnProject) {
                    return baseHeaders
                }

                const headers = [
                    ...baseHeaders,
                ]

                // Колонки приоритетов показываем только для реально существующих каскадов
                if (this.hasCascadeType('mailings')) {
                    headers.push({ text: 'Маркетинговые рассылки', value: 'mailingsPriority', sortable: false, align: 'center' })
                }
                if (this.hasCascadeType('notifications')) {
                    headers.push({
                        text: 'Сервисные уведомления',
                        value: 'notificationsPriority',
                        sortable: false,
                        align: 'center',
                    })
                }
                if (this.hasCascadeType('crmMessages')) {
                    headers.push({
                        text: 'Сообщения из YCLIENTS (коды подтверждения)',
                        value: 'crmMessagesPriority',
                        sortable: false,
                        align: 'center',
                    })
                }
                return headers
            }
            // Если передан allTelephonyCards, это телефония
            if (this.allTelephonyCards) {
                return [
                    { text: 'Телефония', value: 'connectorType', sortable: false },
                    { text: '', value: 'actions', sortable: false, align: 'end', width: '1%' },
                ]
            }
            // Если передан allSourcesCards, это источники данных
            if (this.allSourcesCards) {
                return [
                    { text: 'Источники данных', value: 'connectorType', sortable: false },
                    { text: '', value: 'actions', sortable: false, align: 'end', width: '1%' },
                ]
            }
            // Иначе это источники
            return [
                { text: 'Источники', value: 'connectorType', sortable: false },
                { text: '', value: 'actions', sortable: false, align: 'end', width: '1%' },
            ]
        },
        connectorsAvatars() {
            return this.$store.state.connectorsAvatars
        },
        projectId() {
            return this.$router.currentRoute.params.id
        },
        project() {
            return this.$store.getters.projectById(this.projectId)
        },
        inputVal: {
            get() {
                return this.selectedConnectors
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        tableItems() {
            // Если передан allChannelsCards, используем его (для каналов)
            if (this.allChannelsCards) {
                return this.allChannelsCards.map(card => {
                    if (card.isActive) {
                        // Активный коннектор
                        return card.connector
                    } else {
                        // Неактивная карточка
                        return {
                            isActive: false,
                            channelType: card.channelType,
                            isAdditional: card.isAdditional,
                            futureIndex: card.futureIndex || 1,
                            connectorType: card.channelType, // для совместимости с getConnectorAvatar
                        }
                    }
                })
            }
            // Если передан allTelephonyCards, используем его (для телефонии)
            if (this.allTelephonyCards) {
                return this.allTelephonyCards.map(card => {
                    if (card.isActive) {
                        // Активный коннектор
                        return card.connector
                    } else {
                        // Неактивная карточка
                        return {
                            isActive: false,
                            connectorType: card.connectorType,
                        }
                    }
                })
            }
            // Если передан allSourcesCards, используем его (для источников данных)
            if (this.allSourcesCards) {
                return this.allSourcesCards.map(card => {
                    if (card.isActive) {
                        // Активный коннектор
                        return card.connector
                    } else {
                        // Неактивная карточка
                        return {
                            isActive: false,
                            connectorType: card.connectorType,
                        }
                    }
                })
            }
            // Иначе используем обычные коннекторы (для источников)
            return this.connectors
        },
        isTester() {
            return cfg.testerEmails.includes(this.$auth.user.email)
        },
    },
    watch: {
        selectedConnectors() {
            this.inputVal = this.selectedConnectors
        },
        tableItems: {
            handler() {
                this.prefetchReworkAuthStatuses()
                this.syncSmsGateOnline()
            },
            immediate: true,
        },
        channelPriorities: {
            deep: true,
            handler(newVal) {
                // Синхронизируем props с локальными данными при изменении
                this.localChannelPriorities = { ...newVal }
            },
            immediate: true,
        },
        projectId: {
            async handler(newProjectId, oldProjectId) {
                if (!newProjectId || newProjectId === oldProjectId) return
                this.resetCascadesState()
                if (this.allChannelsCards) {
                    await this.loadChannelPriorities()
                }
            },
        },
    },
    async mounted() {
        // Загружаем приоритеты каналов при монтировании компонента
        if (this.allChannelsCards && this.projectId) {
            await this.loadChannelPriorities()
        }
        
        // Слушаем событие для перезагрузки приоритетов
        this.$root.$on('reload-channel-priorities', this.handleReloadPriorities)
    },
    beforeDestroy() {
        // Отписываемся от события при уничтожении компонента
        this.$root.$off('reload-channel-priorities', this.handleReloadPriorities)
        // Очищаем таймер при уничтожении компонента
        if (this.savePriorityTimer) {
            clearTimeout(this.savePriorityTimer)
        }
        // Очищаем интервал опроса статуса whatsappBasis при уничтожении компонента
        if (this.whatsappBasisAuthInterval) {
            clearInterval(this.whatsappBasisAuthInterval)
            this.whatsappBasisAuthInterval = null
        }
    },
    methods: {
        checkAccess,
        checkRole,
        hasCascadeType(cascadeType) {
            return Boolean(this.cascades?.[cascadeType])
        },
        resetCascadesState() {
            this.cascades = {}
            this.cascadeIds = {}
            this.hasAnyCascadesOnProject = null
            this.hasLoadedCascades = false
            this.localChannelPriorities = {}
        },
        shouldUseReworkAuthStatus(item) {
            if (!item || item.isActive === false) return false
            const reworkProviderTypes = [
                PROVIDER_TYPES.whatsappBasis,
                PROVIDER_TYPES.maxBasis,
                PROVIDER_TYPES.maxDh,
                PROVIDER_TYPES.whatsappApi,
                PROVIDER_TYPES.whatsappDh,
                PROVIDER_TYPES.telegramApi,
            ]
            return reworkProviderTypes.includes(item.providerType)
        },
        getAuthStatusConnectorId(item) {
            return item?.connectorId || item?.id || null
        },
        isAuthStatusLoading(item) {
            if (!this.shouldUseReworkAuthStatus(item)) return false
            const connectorId = this.getAuthStatusConnectorId(item)
            if (!connectorId) return false
            return Boolean(this.channelAuthStatusLoadingByConnectorId[connectorId])
        },
        async fetchReworkAuthStatus(item) {
            const connectorId = this.getAuthStatusConnectorId(item)
            if (!connectorId) return
            if (this.channelAuthStatusLoadingByConnectorId[connectorId]) return
            if (this.channelAuthStatusByConnectorId[connectorId] !== undefined) return

            this.$set(this.channelAuthStatusLoadingByConnectorId, connectorId, true)
            try {
                const { data: authData, error: authError } = await api.networkFixer.getAuthStatusRework({ connectorId })
                if (authError) {
                    this.$set(this.channelAuthStatusByConnectorId, connectorId, null)
                    return
                }

                const isAuthorized = authData === 'authorized'
                this.$set(this.channelAuthStatusByConnectorId, connectorId, isAuthorized)
            } catch (e) {
                this.$set(this.channelAuthStatusByConnectorId, connectorId, null)
                console.error('Ошибка загрузки authStatusRework:', e)
            } finally {
                this.$set(this.channelAuthStatusLoadingByConnectorId, connectorId, false)
            }
        },
        isSmsGateChannel(item) {
            if (!item) return false
            const provider = item.providerType || item.channelType
            return (
                item.connectorType === CHANNEL_TYPES.smsChannel ||
                provider === PROVIDER_TYPES.smsGate ||
                provider === 'sms-gate'
            )
        },
        /**
         * Одна подпись — самая важная причина, по которой канал не работает.
         * Не перечисляем факты («в сети · нет номера»), иначе строка превращается
         * в кашу. Приоритет: нет связи -> нет номера -> всё хорошо.
         */
        smsOnlineLabel(item) {
            if (!this.isSmsGateChannel(item)) return ''
            const connectorId = this.getAuthStatusConnectorId(item)
            const online = this.smsOnlineByConnectorId[connectorId]
            // undefined/null — ещё не знаем, молчим вместо догадок
            if (online === undefined || online === null) return ''
            if (online === false) return 'не в сети'
            if (!this.smsPhoneByConnectorId[connectorId]) return 'нужен номер'
            return 'в сети'
        },
        isSmsChannelProblem(item) {
            const label = this.smsOnlineLabel(item)
            return label === 'не в сети' || label === 'нужен номер'
        },
        isSmsChannelOffline(item) {
            if (!this.isSmsGateChannel(item)) return false
            const connectorId = this.getAuthStatusConnectorId(item)
            // Пугаем только при ЯВНОМ false: undefined/null — ещё не знаем.
            return this.smsOnlineByConnectorId[connectorId] === false
        },
        syncSmsGateOnline() {
            ;(this.tableItems || this.listItems || []).forEach((item) => {
                if (!this.isSmsGateChannel(item)) return
                this.fetchSmsGateOnline(item)
            })
        },
        async fetchSmsGateOnline(item) {
            const connectorId = this.getAuthStatusConnectorId(item)
            if (!connectorId) return
            if (this.smsOnlineLoadingByConnectorId[connectorId]) return

            this.$set(this.smsOnlineLoadingByConnectorId, connectorId, true)
            try {
                const res = await api.networkFixer.getSmsGateStatus({ connectorId })
                this.$set(this.smsOnlineByConnectorId, connectorId, res?.data?.online ?? null)
                // Ошибка ответа — статус неизвестен (null), а не «Не подключен».
                this.$set(
                    this.smsConnectedByConnectorId,
                    connectorId,
                    res?.error || res?.data?.error !== false ? null : isSmsGateStatusAuthorized(res),
                )
                this.$set(this.smsPhoneByConnectorId, connectorId, res?.data?.phone ?? null)
            } catch (e) {
                this.$set(this.smsOnlineByConnectorId, connectorId, null)
                this.$set(this.smsConnectedByConnectorId, connectorId, null)
            } finally {
                this.$set(this.smsOnlineLoadingByConnectorId, connectorId, false)
            }
        },
        prefetchReworkAuthStatuses() {
            if (!this.allChannelsCards) return
            this.tableItems.forEach((item) => {
                if (!this.shouldUseReworkAuthStatus(item)) return
                this.fetchReworkAuthStatus(item)
            })
        },
        async loadWhatsappBasisMetaAndStatus(connectorId) {
            if (!connectorId) return
            try {
                const [app, error] = await api.networkFixer
                    .getApp({ connectorId })
                    .then(res => [res.data?.data, res.error])

                let name = 'WhatsApp *'
                if (!error && app) {
                    // Сохраняем тип провайдера для условия showQrcode
                    this.whatsappBasisChannelType = Object.keys(app?.channel || {})[0] || null
                    if (app?.channel?.['wApi-telegram']?.credentials || app?.channel?.['dh-telegram']?.credentials) {
                        name = 'Telegram *'
                    } else if (app?.channel?.['green-api-max']?.credentials) {
                        name = 'MAX *'
                    }
                }
                this.$set(this.whatsappBasisNames, connectorId, name)
                await this.loadWhatsappBasisStatus(connectorId)
            } catch (e) {
                console.error('Ошибка при загрузке app данных для whatsappBasis:', e)
                this.$set(this.whatsappBasisNames, connectorId, 'WhatsApp *')
            }
        },
        async loadWhatsappBasisStatus(connectorId) {
            if (!connectorId) return
            try {
                const { data: authData, error: authError } = await api.networkFixer.getAuthStatus({
                    connectorId,
                })
                if (!authError) {
                    this.whatsappBasisAuthStatus = authData?.alive ?? null
                }
            } catch (authErr) {
                console.error('Ошибка при загрузке authStatus для whatsappBasis:', authErr)
            }
        },
        isChannelAuthorized(item) {
            // Только для каналов и только для активных коннекторов
            if (!this.allChannelsCards) return false
            if (item.isActive === false) return false
            if (this.isSmsGateChannel(item)) {
                const connected = this.smsConnectedByConnectorId[this.getAuthStatusConnectorId(item)]
                // Живой статус пришёл — верим ему, а не копии alive в документе проекта.
                if (connected !== undefined && connected !== null) return connected
                return item.alive === true
            }
            if (this.shouldUseReworkAuthStatus(item)) {
                const connectorId = this.getAuthStatusConnectorId(item)
                if (!connectorId) return false
                if (this.channelAuthStatusLoadingByConnectorId[connectorId]) return false
                const reworkStatus = this.channelAuthStatusByConnectorId[connectorId]
                if (reworkStatus !== undefined && reworkStatus !== null) {
                    return reworkStatus === true
                }
                return item.alive === true
            }

            // Статус берём из поля alive коннектора
            return item.alive === true
        },
        isHrTelegram(item) {
            return item.connectorType === CONNECTOR_TYPES.telegramHr || item.connectorType === CONNECTOR_TYPES.telegramHrBranch
        },
        isChannelType(item) {
            // Проверяем, является ли элемент каналом из CHANNEL_TYPES
            // Каналы имеют connectorType из CHANNEL_TYPES (whatsappChannel, telegramChannel, maxChannel)
            return Object.values(CHANNEL_TYPES).includes(item.connectorType)
        },
        handleAuthStatusClick(item) {
            // Для неавторизованных каналов открываем настройки канала в сайдбаре
            this.openConnectorSettingsInSidebar(item)
        },
        openConnectorSettingsInSidebar(connector) {
            const connectorId = connector.connectorId || connector.id
            if (connectorId) {
                this.$emit('openConnectorSettingsInSidebar', connectorId)
            } else {
                console.error('openConnectorSettingsInSidebar: connectorId не найден', connector)
            }
        },
        canOpenChannelSettings(item) {
            // Проверяем права на редактирование
            if (!checkRole(this.projectId, 'project:connector:edit')) {
                return false
            }
            // Настройки доступны только для существующих каналов (не draft и не неактивные карточки)
            // Исключаем HR Telegram, так как для них настройки недоступны
            if (this.isHrTelegram(item)) {
                return false
            }
            return item.isActive !== false && !item.isDraft && (item.connectorId || item.id)
        },
        canFinishConnectorCreate(item) {
            // Проверяем права на редактирование
            if (!checkRole(this.projectId, 'project:connector:edit')) {
                return false
            }
            // Завершение подключения доступно только для draft каналов
            return item.isDraft && item.isActive !== false && (item.connectorId || item.id)
        },
        handleChannelClick(item) {
            // При клике на название канала открываем сайдбар с настройками
            if (this.canOpenChannelSettings(item) || this.canFinishConnectorCreate(item)) {
                this.openConnectorSettingsInSidebar(item)
            }
        },
        handleRowClick(item, slot) {
            // При клике на строку канала открываем сайдбар с настройками
            // В Vuetify @click:row передает (item, slot), где item - это элемент строки
            if (!this.allChannelsCards || !item) return
            
            // Проверяем права на редактирование
            if (!checkRole(this.projectId, 'project:connector:edit')) {
                return
            }
            
            // Открываем сайдбар для всех каналов (кроме HR Telegram)
            // Исключаем HR Telegram, так как для них настройки недоступны
            if (this.isHrTelegram(item)) return
            
            // Открываем сайдбар для активных каналов и draft каналов
            if (item.isActive !== false && (item.connectorId || item.id)) {
                this.openConnectorSettingsInSidebar(item)
            }
        },
        getConnectorAvatar(item) {
            // Если это неактивная карточка канала, телефонии или источника данных
            if (item.isActive === false) {
                // Если это телефония (есть connectorType и он в списке телефоний)
                if (item.connectorType && TELEPHONY_CONNECTOR_TYPES.includes(item.connectorType)) {
                    const avatar = this.connectorsAvatars.find(avatar => avatar.type === item.connectorType) || {
                        color: 'white',
                        symbol: '?',
                        name: 'Неизвестно',
                    }
                    return avatar
                }
                
                // Если это источник данных (есть connectorType и он в списке источников)
                if (item.connectorType && GCB_EXTERNAL_CONNECTOR_TYPES.includes(item.connectorType)) {
                    const avatar = this.connectorsAvatars.find(avatar => avatar.type === item.connectorType) || {
                        color: 'white',
                        symbol: '?',
                        name: 'Неизвестно',
                    }
                    return avatar
                }
                
                // Преобразуем PROVIDER_TYPES в CHANNEL_TYPES для поиска аватара
                const channelType = PROVIDER_TO_CHANNEL_MAP[item.channelType]
                const avatar = this.connectorsAvatars.find(avatar => avatar.type === channelType) || {
                    color: 'white',
                    symbol: '?',
                    name: 'Неизвестно',
                }
                
                // Определяем название для неактивной карточки
                const baseNames = {
                    [PROVIDER_TYPES.whatsappBasis]: 'WhatsApp',
                    [PROVIDER_TYPES.whatsappApi]: 'WhatsApp',
                    [PROVIDER_TYPES.whatsapp]: 'WhatsApp',
                    [PROVIDER_TYPES.whatsappDh]: 'WhatsApp',
                    [PROVIDER_TYPES.telegramApi]: 'Telegram',
                    [PROVIDER_TYPES.telegramDh]: 'Telegram',
                    [PROVIDER_TYPES.maxBasis]: 'MAX',
                    [PROVIDER_TYPES.maxDh]: 'MAX',
                    [PROVIDER_TYPES.maxBusiness]: 'MAX Business',
                    [PROVIDER_TYPES.smsGate]: 'SMS',
                }
                const baseName = baseNames[item.channelType] || item.channelType
                
                // Определяем targetChannelType для индексации
                let targetChannelType = null
                if (item.channelType === PROVIDER_TYPES.whatsappApi ||
                    item.channelType === PROVIDER_TYPES.whatsappBasis ||
                    item.channelType === PROVIDER_TYPES.whatsapp ||
                    item.channelType === PROVIDER_TYPES.whatsappDh) {
                    targetChannelType = CHANNEL_TYPES.whatsappChannel
                } else if (item.channelType === PROVIDER_TYPES.telegramApi || 
                           item.channelType === PROVIDER_TYPES.telegramDh) {
                    targetChannelType = CHANNEL_TYPES.telegramChannel
                } else if (item.channelType === PROVIDER_TYPES.maxBasis || item.channelType === PROVIDER_TYPES.maxDh) {
                    targetChannelType = CHANNEL_TYPES.maxChannel
                } else if (item.channelType === PROVIDER_TYPES.maxBusiness) {
                    targetChannelType = CHANNEL_TYPES.maxBusiness
                } else if (item.channelType === PROVIDER_TYPES.smsGate) {
                    targetChannelType = CHANNEL_TYPES.smsChannel
                }
                
                // Вычисляем индекс на основе реальных каналов
                let channelIndex = 1
                if (targetChannelType) {
                    channelIndex = this.getChannelIndex(targetChannelType, null)
                }
                
                let displayName
                if (channelIndex === 1) {
                    // Первый экземпляр без индекса
                    displayName = baseName
                } else {
                    // Остальные с индексом
                    displayName = `${baseName} ${channelIndex}`
                }
                
                return {
                    ...avatar,
                    name: displayName,
                }
            }
            
            // Если это канал (CHANNEL_TYPES или PROVIDER_TYPES), применяем логику индексации
            const isWhatsappBasis = item.connectorType === CONNECTOR_TYPES.whatsappBasis
            
            const isChannel = Object.values(CHANNEL_TYPES).includes(item.connectorType) ||
                             (item.providerType && Object.values(PROVIDER_TYPES).includes(item.providerType)) ||
                             Object.values(PROVIDER_TYPES).includes(item.connectorType)
            
            // Для каналов используем connectorType (из CHANNEL_TYPES) для поиска аватара
            // Для остальных коннекторов используем providerType если есть, иначе connectorType
            const avatarType = isChannel ? item.connectorType : (item.providerType || item.connectorType)
            const baseAvatar = this.connectorsAvatars.find(avatar => avatar.type === avatarType) || {
                color: 'white',
                symbol: '404',
                name: 'Не найдено',
            }
            
            if (isChannel) {
                // Используем providerType если есть, иначе connectorType (для старых коннекторов)
                const providerType = item.providerType || item.connectorType
                
                // Для whatsappBasis не учитываем при индексации, но отображаем со звездочкой
                if (this.project.isChannelsRework && isWhatsappBasis) {
                    // Используем кешированное имя из whatsappBasisNames, если есть
                    const cachedName = this.whatsappBasisNames[item.connectorId]
                    if (cachedName) {
                        // Определяем правильный аватар на основе имени
                        let avatar = baseAvatar
                        if (cachedName.includes('MAX')) {
                            const maxAvatar = this.connectorsAvatars.find(a => a.type === CHANNEL_TYPES.maxChannel)
                            if (maxAvatar) avatar = maxAvatar
                        } else if (cachedName.includes('Telegram')) {
                            const tgAvatar = this.connectorsAvatars.find(a => a.type === CHANNEL_TYPES.telegramChannel)
                            if (tgAvatar) avatar = tgAvatar
                        }
                        return {
                            ...avatar,
                            name: cachedName,
                        }
                    }
                    // Если еще не загружено, возвращаем временное имя
                    return {
                        ...baseAvatar,
                        name: 'WhatsApp *',
                    }
                }
                
                const channelIndex = this.getChannelIndex(providerType, item)
                const displayName = this.getChannelDisplayName(providerType, channelIndex)
                return {
                    ...baseAvatar,
                    name: displayName,
                }
            }
            
            // Обычный коннектор
            return baseAvatar
        },
        showWhatsappBasisQrcode(item) {
            // Показываем QR только для whatsappBasis коннектора
            if (!item || item.connectorType !== CONNECTOR_TYPES.whatsappBasis) return false
            if (!this.whatsappBasisConnectorId || item.connectorId !== this.whatsappBasisConnectorId) return false
            // Условие такое же, как в ConnectorCard.showQrcode
            return ['basis-api', 'wApi-telegram', 'wApi-whatsapp', 'green-api-max'].includes(this.whatsappBasisChannelType)
        },
        shouldShowQrCodeButton(item) {
            // Проверяем, нужно ли показывать кнопку QR-кода для данного элемента
            if (!item || !item.providerType) return false
            const supportedProviderTypes = [
                PROVIDER_TYPES.whatsappBasis,
                PROVIDER_TYPES.whatsappApi,
                PROVIDER_TYPES.whatsappDh,
                PROVIDER_TYPES.telegramApi,
                PROVIDER_TYPES.telegramDh,
                PROVIDER_TYPES.maxBasis,
                PROVIDER_TYPES.maxDh,
                PROVIDER_TYPES.maxBusiness,
            ]
            return supportedProviderTypes.includes(item.providerType)
        },
        getWhatsappBasisStatusIcon(item) {
            if (!item || item.connectorType !== CONNECTOR_TYPES.whatsappBasis) return null
            // В приоритете используем локально загруженный authStatus (строка 'auth' | 'no_auth'),
            // иначе fallback на поле alive коннектора, если в нем лежит тот же статус
            const status = this.whatsappBasisAuthStatus != null
                ? this.whatsappBasisAuthStatus
                : item.alive

            if (status === 'auth') {
                return 'mdi-check-bold'
            }
            if (status === 'no_auth') {
                return 'mdi-exclamation-thick'
            }
            return null
        },
        getWhatsappBasisStatusIconColor(item) {
            const icon = this.getWhatsappBasisStatusIcon(item)
            if (icon === 'mdi-check-bold') {
                return 'status-icon--success'
            }
            if (icon === 'mdi-exclamation-thick') {
                return 'status-icon--error'
            }
            return ''
        },
        async shareWhatsappBasisQrCode(item) {
            if (!item || !item.connectorId) return
            const res = await api.networkFixer.getNoAuthToken({ connectorId: item.connectorId })
            let token
            if (!res.error) {
                token = res.data.token
                const project = this.project
                const isChannelsRework = project?.isChannelsRework ? 'true' : 'false'
                
                // Определяем provider в зависимости от isChannelsRework
                let provider
                if (project?.isChannelsRework) {
                    // При isChannelsRework используем providerType с маппингом
                    // Ссылка «Поделиться QR-кодом» ведёт на наш провайдер: у стороннего
                    // канал заберёт проверка в getDhWaQr/getDhMaxQr, как у телеграма.
                    const FORCED_PROVIDER_ROUTE = {
                        [PROVIDER_TYPES.whatsappBasis]: 'basis-api',
                        [PROVIDER_TYPES.whatsappApi]: 'wApi-whatsapp',
                        [PROVIDER_TYPES.telegramApi]: 'wApi-telegram',
                        [PROVIDER_TYPES.maxBasis]: 'green-api-max',
                    }
                    const providerTypeMap = {
                        [PROVIDER_TYPES.whatsappBasis]: 'dh-whatsapp',
                        [PROVIDER_TYPES.whatsappApi]: 'dh-whatsapp',
                        [PROVIDER_TYPES.whatsappDh]: 'dh-whatsapp',
                        [PROVIDER_TYPES.telegramApi]: 'dh-telegram',
                        [PROVIDER_TYPES.telegramDh]: 'dh-telegram',
                        [PROVIDER_TYPES.maxBasis]: 'dh-max',
                        [PROVIDER_TYPES.maxDh]: 'dh-max',
                    }
                    // Рычаг поддержки: если провайдера назначили руками, ссылка ведёт к нему
                    const forced = item.forcedProvider
                    provider = (forced && FORCED_PROVIDER_ROUTE[forced])
                        || providerTypeMap[item.providerType]
                        || this.whatsappBasisChannelType
                } else {
                    // При старом режиме используем whatsappBasisChannelType
                    provider = this.whatsappBasisChannelType
                }
                
                const qrCodeUrl = `${window.location.origin}/qr/${item.connectorId}?token=${token}&isChannelsRework=${isChannelsRework}&provider=${provider}`
                await navigator.clipboard.writeText(qrCodeUrl)
                window.open(qrCodeUrl, '_blank')
                this.$store.dispatch('callNotify', 'Ссылка скопирована в буфер обмена')
            } else {
                this.$store.dispatch('callNotify', 'Ошибка при генерации ссылки')
            }
        },
        getChannelIndex(channelType, connector) {
            // Индексируем только каналы с connectorType из CHANNEL_TYPES
            // Определяем группу на основе CHANNEL_TYPES
            let targetChannelType = null
            if (channelType === CHANNEL_TYPES.whatsappChannel ||
                channelType === PROVIDER_TYPES.whatsappApi ||
                channelType === PROVIDER_TYPES.whatsappBasis ||
                channelType === PROVIDER_TYPES.whatsapp ||
                channelType === PROVIDER_TYPES.whatsappDh) {
                targetChannelType = CHANNEL_TYPES.whatsappChannel
            } else if (channelType === CHANNEL_TYPES.telegramChannel || 
                       channelType === PROVIDER_TYPES.telegramApi || 
                       channelType === PROVIDER_TYPES.telegramDh) {
                targetChannelType = CHANNEL_TYPES.telegramChannel
            } else if (channelType === CHANNEL_TYPES.maxChannel || 
                       channelType === PROVIDER_TYPES.maxBasis ||
                       channelType === PROVIDER_TYPES.maxDh) {
                targetChannelType = CHANNEL_TYPES.maxChannel
            } else if (
                channelType === CHANNEL_TYPES.maxBusiness ||
                channelType === PROVIDER_TYPES.maxBusiness
            ) {
                targetChannelType = CHANNEL_TYPES.maxBusiness
            } else if (
                channelType === CHANNEL_TYPES.smsChannel ||
                channelType === PROVIDER_TYPES.smsGate
            ) {
                targetChannelType = CHANNEL_TYPES.smsChannel
            } else {
                return 1
            }
            
            // Находим все подключенные каналы с connectorType из CHANNEL_TYPES, сортируем по дате создания
            const channelsInGroup = this.project.connectors
                .filter(c => {
                    if (!c.isChisai) {
                        // Индексируем только каналы с connectorType из CHANNEL_TYPES
                        // Все WhatsApp каналы имеют connectorType === CHANNEL_TYPES.whatsappChannel
                        return c.connectorType === targetChannelType
                    }
                    return false
                })
                .sort((a, b) => {
                    // Если createdAt отсутствует или равен 0, используем очень большое число,
                    // чтобы такие каналы попадали в конец списка (они новые и еще не сохранены на сервере)
                    const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                    const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                    // Если оба имеют валидный createdAt, сортируем по нему
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) {
                        return aTime - bTime
                    }
                    // Если один из них не имеет createdAt, он должен быть в конце
                    if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) {
                        return 1
                    }
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) {
                        return -1
                    }
                    // Если оба не имеют createdAt, сортируем по connectorId (лексикографически)
                    return a.connectorId.localeCompare(b.connectorId)
                })
            
            // Определяем индекс текущего канала (начиная с 1)
            // Если connector передан, ищем его индекс
            if (connector) {
                const currentIndex = channelsInGroup.findIndex(c => c.connectorId === connector.connectorId)
                return currentIndex >= 0 ? currentIndex + 1 : channelsInGroup.length + 1
            }
            // Если connector не передан (для неактивных карточек), возвращаем следующий индекс
            return channelsInGroup.length + 1
        },
        handleReloadPriorities() {
            // Перезагружаем приоритеты при получении события
            if (this.allChannelsCards && this.projectId) {
                const isChannelCreationMode = !!this.$store.state.channelCreationMode
                if (isChannelCreationMode) {
                    this.$store.commit('startChannelCreationLock')
                }
                // Очищаем кеш каскадов, чтобы загрузить свежие данные
                this.resetCascadesState()
                Promise.resolve()
                    .then(() => this.loadChannelPriorities())
                    .finally(() => {
                        if (isChannelCreationMode) {
                            this.$store.commit('endChannelCreationLock')
                            this.$root.$emit('channel-create-priorities-sync-done')
                        }
                    })
            }
        },
        getChannelDisplayName(channelType, index) {
            const baseNames = {
                [PROVIDER_TYPES.whatsappBasis]: 'WhatsApp',
                [PROVIDER_TYPES.whatsappApi]: 'WhatsApp',
                [PROVIDER_TYPES.whatsapp]: 'WhatsApp',
                [PROVIDER_TYPES.whatsappDh]: 'WhatsApp',
                [PROVIDER_TYPES.telegramApi]: 'Telegram',
                [PROVIDER_TYPES.telegramDh]: 'Telegram',
                [PROVIDER_TYPES.maxBasis]: 'MAX',
                [PROVIDER_TYPES.maxDh]: 'MAX',
                [PROVIDER_TYPES.maxBusiness]: 'MAX Business',
                [PROVIDER_TYPES.smsGate]: 'SMS',
            }
            const baseName = baseNames[channelType] || channelType
            
            // Первый экземпляр (index === 1) без индекса, остальные с индексом
            if (index === 1) {
                return baseName
            }
            
            // Остальные экземпляры с индексом
            return `${baseName} ${index}`
        },
        deleteConnector(connector) {
            this.$parent.$emit('deleteConnector', connector.connectorId)
        },
        openConnectorSettings(connector) {
            const connectorId = connector.connectorId || connector.id
            if (connectorId) {
                this.$parent.$emit('openConnectorSettings', connectorId)
            }
        },
        finishConnectorCreate(connector) {
            this.$store.commit('project/setLoggedAddingConnector', connector)
            this.$store.commit('project/updateCurrentAddingConnector', connector)
            
            // Проверяем, является ли коннектор каналом
            const isChannel = Object.values(CHANNEL_TYPES).includes(connector.connectorType)
            
            if (isChannel) {
                this.$router.push(`/project/${connector.projectId}/addChannelStage3`)
            } else {
                this.$router.push(`/project/${connector.projectId}/addConnectorStage3`)
            }
        },
        connectInactiveChannel(item) {
            // Если идет критичный процесс создания/сохранения — не даём стартовать новый поток
            const lockCount = this.$store.state.channelCreationLockCount || 0
            if (lockCount > 0) {
                this.$store.dispatch('callNotify', 'Дождитесь завершения создания канала и установки приоритетов')
                return
            }
            // Если это не каналы (allChannelsCards), используем старую логику с переходом на страницу
            if (!this.allChannelsCards) {
                const path = this.$router.currentRoute.path.split('/').slice(0, 3).join('/')
                
                // Если это телефония
                if (item.connectorType && TELEPHONY_CONNECTOR_TYPES.includes(item.connectorType)) {
                    // Переходим на страницу добавления коннектора с предвыбранным типом телефонии
                    this.$router.push(`${path}/addConnectorStage1?type=${item.connectorType}`)
                    return
                }
                
                // Если это источник данных
                if (item.connectorType && GCB_EXTERNAL_CONNECTOR_TYPES.includes(item.connectorType)) {
                    // Переходим на страницу добавления коннектора с предвыбранным типом источника данных
                    this.$router.push(`${path}/addConnectorStage1?type=${item.connectorType}`)
                    return
                }
                return
            }

            // Для каналов (allChannelsCards) сначала открываем сайдбар оплаты доп. канала,
            // если биллинг для проекта включен.
            // if (this.project && this.project.billingEnabled) {
            //     const billingStore = useBillingStore()
            //     // На случай, если экшен еще не подцепился (старый бандл) — не падаем
            //     if (typeof billingStore.setAddChannelContext === 'function') {
            //         const avatar = this.getConnectorAvatar(item)
            //         billingStore.setAddChannelContext({
            //             name: avatar.name,
            //             icon: avatar.image || null,
            //         })
            //     }
            //     billingStore.setPaymentModalOpened(true, false)
            //     return
            // }

            // Иначе (если биллинга нет) — старая логика открытия сайдбара настроек канала
            // Определяем группу по типу канала
            // Для неактивных каналов используем channelType, если его нет - используем connectorType
            const channelType = item.channelType || item.connectorType
            
            const channelGroups = {
                [PROVIDER_TYPES.whatsappBasis]: 'whatsapp',
                [PROVIDER_TYPES.whatsappApi]: 'whatsapp',
                [PROVIDER_TYPES.whatsapp]: 'whatsapp',
                [PROVIDER_TYPES.whatsappDh]: 'whatsapp',
                [PROVIDER_TYPES.telegramApi]: 'telegram',
                [PROVIDER_TYPES.telegramDh]: 'telegram',
                [PROVIDER_TYPES.maxBasis]: 'max',
                [PROVIDER_TYPES.maxDh]: 'max',
                [PROVIDER_TYPES.maxBusiness]: 'maxBusiness',
                [PROVIDER_TYPES.smsGate]: 'sms',
            }
            
            const groupId = channelGroups[channelType]
            
            // Всегда открываем сайдбар для создания канала (для allChannelsCards)
            this.$store.commit('setChannelCreationMode', true)
            this.$store.commit('setChannelCreationStage', 1)
            // ВАЖНО: если store уже считает сайдбар открытым, commit(true) не изменит значение,
            // а UI мог закрыться из-за внешнего клика/ESC. Делаем "force reopen".
            const wasOpen = !!this.$store.state.isConnectorSettingsSidebar
            if (wasOpen) {
                this.$store.commit('setIsConnectorSettingsSidebar', false)
                this.$nextTick(() => {
                    this.$store.commit('setIsConnectorSettingsSidebar', true)
                })
            } else {
                this.$store.commit('setIsConnectorSettingsSidebar', true)
            }
            
            // Устанавливаем query параметр для фильтрации списка каналов (без изменения роута)
            // Используем window.history для обновления query без перезагрузки страницы
            if (groupId && this.$route.query.group !== groupId) {
                const newUrl = new URL(window.location.href)
                newUrl.searchParams.set('group', groupId)
                window.history.replaceState({}, '', newUrl.toString())
            }
        },
        getChannelPriority(connectorId, type) {
            // Сначала проверяем локальные приоритеты, затем из props
            const localPriority = this.localChannelPriorities[connectorId]?.[type]
            if (localPriority !== undefined && localPriority !== null) {
                return localPriority
            }

            const propPriority = this.channelPriorities[connectorId]?.[type]
            if (propPriority !== undefined && propPriority !== null) {
                return propPriority
            }

            return null
        },
        /**
         * Получить каскад для типа (создание каскадов с фронта запрещено)
         */
        async getOrCreateCascade(cascadeType) {
            // Проверяем кеш (включая явно закешированное отсутствие)
            if (Object.prototype.hasOwnProperty.call(this.cascades || {}, cascadeType)) {
                return this.cascades[cascadeType] || null
            }

            // Загружаем все каскады проекта (одним запросом) и заполняем кеш по cascadeType
            const { data: cascades, error } = await getCascadesByProject(this.projectId)
            this.hasLoadedCascades = true
            if (error) {
                console.error('Ошибка загрузки каскадов:', error)
                return null
            }
            // Запоминаем наличие каскадов на проекте
            this.hasAnyCascadesOnProject = Boolean((cascades || []).length > 0)

            // Заполняем кеш для всех полученных каскадов
            ;(cascades || []).forEach((c) => {
                if (!c || !c.cascadeType) return
                this.$set(this.cascades, c.cascadeType, c)
                if (c.id) this.$set(this.cascadeIds, c.cascadeType, c.id)
            })

            const cascade = (cascades || []).find((c) => c && c.cascadeType === cascadeType) || null

            // Кешируем отсутствие, чтобы не дергать API повторно для этого типа
            if (!cascade) {
                this.$set(this.cascades, cascadeType, null)
            }

            return cascade
        },
        
        /**
         * Преобразовать каскады в формат приоритетов
         */
        convertCascadesToPriorities(cascades) {
            const priorities = {}
            
            // Получаем проект для поиска коннекторов по channelId
            const project = this.$store.getters.projectById(this.projectId)
            if (!project || !project.connectors) {
                return priorities
            }
            
            const cascadeTypes = this.hasYClientsAndBotConnector
                ? ['mailings', 'notifications', 'crmMessages']
                : this.hasBotConnector
                    ? ['mailings', 'notifications']
                    : ['mailings']
            cascadeTypes.forEach(type => {
                const cascade = cascades[type]
                if (cascade && cascade.channels) {
                    cascade.channels.forEach(channel => {
                        // channel.channelId - это числовой ID канала из networkFixer
                        // Нужно найти коннектор, у которого channelId совпадает
                        const channelId = channel.channelId
                        const connector = project.connectors.find(c => c.channelId === channelId)
                        
                        if (connector) {
                            const connectorId = connector.connectorId || connector.id
                            if (connectorId) {
                                if (!priorities[connectorId]) {
                                    priorities[connectorId] = {}
                                }
                                priorities[connectorId][type] = channel.priority
                            }
                        }
                    })
                }
            })
            
            return priorities
        },
        
        async loadChannelPriorities() {
            if (!this.projectId || this.isLoadingPriorities) return

            this.isLoadingPriorities = true
            try {
                // Загружаем или создаем каскады для всех типов
                const cascadeTypes = this.hasYClientsAndBotConnector
                    ? ['mailings', 'notifications', 'crmMessages']
                    : this.hasBotConnector
                        ? ['mailings', 'notifications']
                        : ['mailings']
                const cascadesData = {}
                
                for (const type of cascadeTypes) {
                    const cascade = await this.getOrCreateCascade(type)
                    if (cascade) {
                        cascadesData[type] = cascade
                    }
                }
                
                // Преобразуем каскады в формат приоритетов
                const priorities = this.convertCascadesToPriorities(cascadesData)
                
                if (Object.keys(priorities).length > 0) {
                    // Объединяем загруженные приоритеты с props (props имеют приоритет, если переданы)
                    this.localChannelPriorities = {
                        ...priorities,
                        ...this.channelPriorities,
                    }
                    // Эмитим событие для синхронизации с родительским компонентом
                    this.$emit('priorities-loaded', this.localChannelPriorities)

                    // Если есть DH WhatsApp — нормализуем приоритеты маркетинговых рассылок,
                    // чтобы "заблокированный" приоритет всегда был последним и не дублировался.
                    if (this.hasAnyCascadesOnProject !== false) {
                        await this.normalizeMailingsPrioritiesForDh()
                    }
                } else if (Object.keys(this.channelPriorities).length > 0) {
                    // Если данных нет в API, но есть в props, используем props
                    this.localChannelPriorities = { ...this.channelPriorities }
                    if (this.hasAnyCascadesOnProject !== false) {
                        await this.normalizeMailingsPrioritiesForDh()
                    }
                }
            } catch (err) {
                console.error('Ошибка при загрузке приоритетов каналов:', err)
                // При ошибке используем приоритеты из props, если они есть
                if (Object.keys(this.channelPriorities).length > 0) {
                    this.localChannelPriorities = { ...this.channelPriorities }
                }
            } finally {
                this.isLoadingPriorities = false
            }
        },
        handleMailingsPriorityInput(item, priority) {
            const connectorId = item?.connectorId || item?.id
            if (
                !this.maxRiskModalOpen &&
                priority !== null &&
                priority !== undefined &&
                isRegularMaxChannel(item)
            ) {
                this.maxRiskPending = { connectorId, type: 'mailings', priority }
                this.maxRiskContext = 'Основной каскад · Маркетинговые рассылки'
                this.maxRiskModalOpen = true
                return
            }
            this.updateChannelPriority(connectorId, 'mailings', priority)
        },
        acceptMaxRisk() {
            const pending = this.maxRiskPending
            this.maxRiskPending = null
            this.maxRiskModalOpen = false
            if (!pending) return
            this.updateChannelPriority(pending.connectorId, pending.type, pending.priority)
        },
        removeMaxFromMailings() {
            const pending = this.maxRiskPending
            this.maxRiskPending = null
            this.maxRiskModalOpen = false
            if (!pending) return
            this.updateChannelPriority(pending.connectorId, pending.type, null)
        },
        dismissMaxRisk() {
            this.maxRiskPending = null
            this.maxRiskModalOpen = false
        },
        updateChannelPriority(connectorId, type, priority) {
            if (!connectorId) return
            
            const item = this.tableItems.find(
                item => (item.connectorId || item.id) === connectorId
            )
            
            // Для маркетинговых рассылок проверяем, не заблокирован ли приоритет для этого канала
            if (type === 'mailings' && item && this.isMailingsPriorityLocked(item)) {
                // Приоритет заблокирован, не обновляем
                return
            }
            
            // Для сервисных уведомлений проверяем, не является ли это канал WhatsApp с провайдером Data Heroes
            if (type === 'notifications' && item && this.isWhatsappDhChannel(item)) {
                // Приоритет заблокирован для таких каналов, не обновляем
                return
            }

            if (!this.localChannelPriorities[connectorId]) {
                this.$set(this.localChannelPriorities, connectorId, {})
            }

            const currentPriority = this.getChannelPriority(connectorId, type)
            const newPriority = priority

            // Если приоритет не изменился, ничего не делаем
            if (currentPriority === newPriority) {
                return
            }

            // Получаем все активные каналы для этого типа приоритета
            const activeChannels = this.tableItems.filter(
                item => item.isActive !== false && (item.connectorId || item.id) && this.isChannelType(item)
            )

            // Создаем копию текущих приоритетов для этого типа
            const prioritiesCopy = {}
            activeChannels.forEach(item => {
                const id = item.connectorId || item.id
                if (id) {
                    const itemPriority = this.getChannelPriority(id, type)
                    if (itemPriority !== null && itemPriority !== undefined) {
                        prioritiesCopy[id] = itemPriority
                    }
                }
            })

            // Если приоритет удаляется (newPriority === null)
            if (newPriority === null || newPriority === undefined) {
                // Удаляем приоритет для текущего канала
                delete prioritiesCopy[connectorId]
                
                // Пересчитываем все приоритеты по порядку без пропусков
                this.normalizePriorities(prioritiesCopy, activeChannels, type, connectorId)
                return
            }

            // Если текущий приоритет был null (не выбран), нужно проверить, не занят ли новый приоритет
            if (currentPriority === null || currentPriority === undefined) {
                // Находим канал, который уже имеет этот приоритет (проверяем до установки приоритета для текущего канала)
                const channelWithSamePriority = activeChannels.find(item => {
                    const id = item.connectorId || item.id
                    if (id && id !== connectorId && prioritiesCopy[id] === newPriority) {
                        return true
                    }
                    return false
                })

                // Если приоритет уже занят, смещаем канал с этим приоритетом и все последующие на +1
                if (channelWithSamePriority) {
                    activeChannels.forEach(item => {
                        const id = item.connectorId || item.id
                        if (id && id !== connectorId && prioritiesCopy[id] !== undefined) {
                            if (prioritiesCopy[id] >= newPriority) {
                                prioritiesCopy[id] += 1
                            }
                        }
                    })
                }
                
                // Устанавливаем приоритет для текущего канала после смещения
                prioritiesCopy[connectorId] = newPriority
            } else {
                // Устанавливаем новый приоритет для текущего канала
                prioritiesCopy[connectorId] = newPriority
                // Применяем логику смещения при изменении приоритета
                const isMovingUp = newPriority < currentPriority

                if (isMovingUp) {
                    // Движение вверх: смещаем все каналы с приоритетом >= newPriority и < currentPriority на +1
                    activeChannels.forEach(item => {
                        const id = item.connectorId || item.id
                        if (id && id !== connectorId && prioritiesCopy[id] !== undefined) {
                            if (prioritiesCopy[id] >= newPriority && prioritiesCopy[id] < currentPriority) {
                                prioritiesCopy[id] += 1
                            }
                        }
                    })
                } else {
                    // Движение вниз: смещаем все каналы с приоритетом > currentPriority и <= newPriority на -1
                    activeChannels.forEach(item => {
                        const id = item.connectorId || item.id
                        if (id && id !== connectorId && prioritiesCopy[id] !== undefined) {
                            if (prioritiesCopy[id] > currentPriority && prioritiesCopy[id] <= newPriority) {
                                prioritiesCopy[id] -= 1
                            }
                        }
                    })
                }
            }

            // Пересчитываем все приоритеты по порядку без пропусков
            this.normalizePriorities(prioritiesCopy, activeChannels, type, connectorId)
        },
        normalizePriorities(prioritiesCopy, activeChannels, type, changedConnectorId) {
            // Собираем все каналы с их приоритетами
            const channelsWithPriorities = activeChannels
                .map(item => ({
                    id: item.connectorId || item.id,
                    priority: prioritiesCopy[item.connectorId || item.id] || null
                }))
                .filter(item => item.id && (item.priority !== null && item.priority !== undefined))

            // Сортируем каналы по приоритету (сохраняем относительный порядок)
            channelsWithPriorities.sort((a, b) => a.priority - b.priority)

            // Пересчитываем приоритеты по порядку: 1, 2, 3, 4... без пропусков
            const normalizedPriorities = {}
            channelsWithPriorities.forEach((item, index) => {
                normalizedPriorities[item.id] = index + 1
            })

            this.applyNormalizedPriorities(normalizedPriorities, activeChannels, type, changedConnectorId)
        },
        applyNormalizedPriorities(normalizedPriorities, activeChannels, type, changedConnectorId) {

            // Применяем нормализованные приоритеты
            Object.keys(normalizedPriorities).forEach(id => {
                if (!this.localChannelPriorities[id]) {
                    this.$set(this.localChannelPriorities, id, {})
                }
                this.$set(this.localChannelPriorities[id], type, normalizedPriorities[id])
                
                // Эмитим событие для каждого измененного канала
                this.$emit('update-channel-priority', {
                    connectorId: id,
                    type,
                    priority: normalizedPriorities[id],
                })
            })

            // Если приоритет был удален, очищаем его
            if (changedConnectorId && !normalizedPriorities[changedConnectorId]) {
                if (this.localChannelPriorities[changedConnectorId]) {
                    this.$set(this.localChannelPriorities[changedConnectorId], type, null)
                }
                this.$emit('update-channel-priority', {
                    connectorId: changedConnectorId,
                    type,
                    priority: null,
                })
            }

            // Сохраняем через API с debounce (500ms)
            if (this.savePriorityTimer) {
                clearTimeout(this.savePriorityTimer)
            }

            this.savePriorityTimer = setTimeout(async () => {
                await this.saveChannelPrioritiesBatch(normalizedPriorities, activeChannels, type, changedConnectorId)
            }, 500)
        },
        async saveChannelPrioritiesBatch(normalizedPriorities, activeChannels, type, changedConnectorId) {
            if (!this.projectId) return

            this.$store.commit('startChannelCreationLock')
            try {
                // Получаем или создаем каскад для типа
                const cascade = await this.getOrCreateCascade(type)
                if (!cascade) {
                    this.$store.dispatch('callNotify', 'Каскад не найден. Создайте каскад на сервере и повторите.')
                    return
                }
                const cascadeId = cascade.id

                // Собираем полный список изменений: нормализованные + удаление (если было)
                const toSave = []
                Object.keys(normalizedPriorities).forEach((connectorId) => {
                    toSave.push({ connectorId, priority: normalizedPriorities[connectorId] })
                })
                if (changedConnectorId && !normalizedPriorities[changedConnectorId]) {
                    toSave.push({ connectorId: changedConnectorId, priority: null })
                }

                // Преобразуем connectorId -> channelId (создаём channelId при необходимости)
                const ensureChannelId = async (connectorId) => {
                    let connector = this.$store.getters['connectorById'](connectorId)
                    if (!connector) return null

                    let channelId = connector.channelId
                    if (!channelId) {
                        const { data: createdChannelId, error: createError } = await createChannel({ connectorId })
                        if (createError || !createdChannelId) return null
                        channelId = createdChannelId

                        // даём бэку время обновить коннектор, затем подтягиваем
                        await new Promise(resolve => setTimeout(resolve, 300))
                        try {
                            const { data: fetchedConnector } = await fetchConnector(connectorId)
                            if (fetchedConnector) {
                                this.$store.commit('updateConnector', fetchedConnector)
                                connector = fetchedConnector
                                channelId = fetchedConnector.channelId || channelId
                            }
                        } catch (e) {
                            // ignore
                        }
                    }
                    return channelId
                }

                const updates = []
                const deletions = []

                for (const item of toSave) {
                    const channelId = await ensureChannelId(item.connectorId)
                    if (!channelId) continue
                    if (item.priority === null || item.priority === undefined) {
                        deletions.push({ connectorId: item.connectorId, channelId })
                    } else {
                        updates.push({ channelId, priority: item.priority })
                    }
                }

                // Одним запросом обновляем все приоритеты (как в CASCADE_API.md)
                if (updates.length > 0) {
                    const { error } = await updateCascadeChannelsPriorities({
                        cascadeId,
                        channels: updates,
                    })
                    if (error) {
                        console.error('Ошибка массового сохранения приоритетов:', error)
                        this.$store.dispatch('callNotify', 'Ошибка сохранения приоритетов каналов')
                    } else {
                        // Обновляем кеш каскада
                        const updatedCascade = await getCascadeById(cascadeId)
                        if (updatedCascade.data) {
                            this.$set(this.cascades, type, updatedCascade.data)
                        }
                    }
                }

                // Удаления (если были) — отдельными запросами, т.к. bulk endpoint не описывает удаление
                if (deletions.length > 0) {
                    for (const d of deletions) {
                        const latestCascade = this.cascades?.[type] || cascade
                        const cascadeChannel = latestCascade?.channels?.find(ch => ch.channelId === d.channelId)
                        if (cascadeChannel) {
                            await deleteCascadeChannel(cascadeChannel.id)
                        }
                    }
                    const updatedCascade = await getCascadeById(cascadeId)
                    if (updatedCascade.data) {
                        this.$set(this.cascades, type, updatedCascade.data)
                    }
                }
            } catch (e) {
                console.error('saveChannelPrioritiesBatch error:', e)
            } finally {
                this.$store.commit('endChannelCreationLock')
            }
        },
        async saveChannelPriority(connectorId, type, priority) {
            if (!this.projectId || !connectorId) return

            try {
                // Получаем коннектор для получения channelId
                let connector = this.$store.getters['connectorById'](connectorId)
                if (!connector) {
                    console.error('Коннектор не найден:', connectorId)
                    return
                }
                
                // API каскадов ожидает числовой channelId, а не connectorId
                let channelId = connector.channelId
                
                // Если у коннектора нет channelId, создаем канал
                if (!channelId) {
                    console.log('У коннектора отсутствует channelId, создаем канал:', connectorId)
                    const { data: createdChannelId, error: createError } = await createChannel({ connectorId })
                    
                    if (createError || !createdChannelId) {
                        console.error('Ошибка создания канала:', createError)
                        this.$store.dispatch('callNotify', 'Ошибка создания канала для назначения приоритета')
                        return
                    }
                    
                    // Используем созданный channelId напрямую
                    // Бэкенд автоматически обновляет коннектор с channelId после создания канала
                    channelId = createdChannelId
                    
                    // Небольшая задержка, чтобы бэкенд успел обновить коннектор
                    await new Promise(resolve => setTimeout(resolve, 300))
                    
                    // Обновляем коннектор в store для актуальности данных (с повторной попыткой)
                    let attempts = 0
                    const maxAttempts = 3
                    while (attempts < maxAttempts) {
                        try {
                            const { data: fetchedConnector } = await fetchConnector(connectorId)
                            if (fetchedConnector && fetchedConnector.channelId) {
                                this.$store.commit('updateConnector', fetchedConnector)
                                connector = fetchedConnector
                                channelId = fetchedConnector.channelId || channelId
                                break
                            }
                        } catch (e) {
                            console.warn(`Попытка ${attempts + 1} обновления коннектора не удалась:`, e)
                        }
                        attempts++
                        if (attempts < maxAttempts) {
                            await new Promise(resolve => setTimeout(resolve, 200))
                        }
                    }
                    
                    if (!channelId) {
                        console.error('Не удалось получить channelId после создания канала:', connectorId)
                        this.$store.dispatch('callNotify', 'Ошибка получения channelId после создания канала')
                        return
                    }
                }
                
                // Получаем или создаем каскад для типа
                const cascade = await this.getOrCreateCascade(type)
                if (!cascade) {
                    console.error('Не удалось получить каскад для типа:', type)
                    this.$store.dispatch('callNotify', 'Каскад не найден. Создайте каскад на сервере и повторите.')
                    return
                }
                
                const cascadeId = cascade.id
                
                // Если приоритет null, удаляем канал из каскада
                if (priority === null) {
                    // Находим канал в каскаде по числовому channelId
                    const cascadeChannel = cascade.channels?.find(ch => ch.channelId === channelId)
                    if (cascadeChannel) {
                        const { error } = await deleteCascadeChannel(cascadeChannel.id)
                        if (error) {
                            console.error('Ошибка удаления канала из каскада:', error)
                        } else {
                            // Обновляем кеш каскада
                            const updatedCascade = await getCascadeById(cascadeId)
                            if (updatedCascade.data) {
                                this.$set(this.cascades, type, updatedCascade.data)
                            }
                        }
                    }
                    return
                }
                
                // Обновляем приоритет канала в каскаде (используем числовой channelId)
                const { data, error } = await updateCascadeChannelPriority({
                    cascadeId,
                    channelId: channelId,
                    priority,
                })
                
                if (error) {
                    console.error('Ошибка сохранения приоритета канала:', error)
                    this.$store.dispatch('callNotify', 'Ошибка сохранения приоритета канала')
                } else {
                    // Обновляем кеш каскада
                    const updatedCascade = await getCascadeById(cascadeId)
                    if (updatedCascade.data) {
                        this.$set(this.cascades, type, updatedCascade.data)
                    }
                }
            } catch (err) {
                console.error('Ошибка при сохранении приоритета канала:', err)
                this.$store.dispatch('callNotify', 'Ошибка при сохранении приоритета канала')
            }
        },
        getMaxPriorityForType(type) {
            // Возвращаем максимальный выбранный приоритет для данного типа
            // Если ни один приоритет не выбран, возвращаем 0
            const activeChannels = this.tableItems.filter(
                item => item.isActive !== false && (item.connectorId || item.id) && this.isChannelType(item)
            )

            let maxPriority = 0
            activeChannels.forEach(item => {
                const id = item.connectorId || item.id
                if (id) {
                    const priority = this.getChannelPriority(id, type)
                    if (priority !== null && priority !== undefined && priority > maxPriority) {
                        maxPriority = priority
                    }
                }
            })

            return maxPriority
        },
        isWhatsappDhChannel(item) {
            // Проверяем, является ли канал WhatsApp с провайдером whatsapp (Data Heroes)
            // Проверяем providerType напрямую или через connector, если item - это connector
            if (item.providerType === PROVIDER_TYPES.whatsapp) {
                return true
            }
            // Если это connector из таблицы, проверяем его providerType
            if (item.connector && item.connector.providerType === PROVIDER_TYPES.whatsapp) {
                return true
            }
            // Если connectorType это whatsappChannel, но нужно проверить providerType из connector
            if (item.connectorType === CHANNEL_TYPES.whatsappChannel) {
                // Пытаемся получить connector из store для проверки providerType
                const connector = this.$store.getters['connectorById'](item.connectorId || item.id)
                if (connector && connector.providerType === PROVIDER_TYPES.whatsapp) {
                    return true
                }
            }
            return false
        },
        getFirstWhatsappDhChannel() {
            // Находим "первый" WhatsApp(DH) канал (по createdAt, затем connectorId), чтобы он всегда занимал последний приоритет
            const whatsappDhChannels = this.tableItems.filter(
                item => item.isActive !== false && 
                       (item.connectorId || item.id) &&
                       this.isWhatsappDhChannel(item)
            )
            
            if (whatsappDhChannels.length === 0) return null
            
            // Сортируем по времени создания (старые первыми), затем по connectorId
            whatsappDhChannels.sort((a, b) => {
                const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                if (aTime !== bTime) return aTime - bTime
                return String(a.connectorId || a.id).localeCompare(String(b.connectorId || b.id))
            })
            
            return whatsappDhChannels[0]
        },
        /**
         * Возвращает "заблокированный" приоритет для WhatsApp(DH) в колонке mailings:
         * - это (max установленного приоритета среди НЕ-DH) + 1
         * - минимум 2, если есть хотя бы один НЕ-DH канал (чтобы можно было выбрать приоритет 1 для первого канала)
         */
        getDhLockedMailingsPriority(activeChannelsOverride) {
            const activeChannels = activeChannelsOverride || this.tableItems.filter(
                item => item.isActive !== false && (item.connectorId || item.id) && this.isChannelType(item)
            )

            const nonDh = activeChannels.filter(item => !this.isWhatsappDhChannel(item))
            const hasNonDh = nonDh.length > 0

            // max среди текущих установленных приоритетов НЕ-DH
            let maxNonDh = 0
            nonDh.forEach((item) => {
                const id = item.connectorId || item.id
                if (!id) return
                const p = this.getChannelPriority(id, 'mailings')
                if (p !== null && p !== undefined && p > maxNonDh) {
                    maxNonDh = p
                }
            })

            if (!hasNonDh) return 1
            return Math.max(2, maxNonDh + 1)
        },
        isMailingsPriorityLocked(item) {
            // Приоритет заблокирован для всех каналов WhatsApp с провайдером Data Heroes
            if (!this.isWhatsappDhChannel(item)) return false
            
            const firstChannel = this.getFirstWhatsappDhChannel()
            if (!firstChannel) return false
            
            return true
        },
        getMailingsPriorityForWhatsappDh(item) {
            // Если это канал WhatsApp с провайдером Data Heroes
            if (this.isWhatsappDhChannel(item)) {
                const firstChannel = this.getFirstWhatsappDhChannel()
                if (!firstChannel) return null
                
                const currentId = item.connectorId || item.id
                const firstId = firstChannel.connectorId || firstChannel.id
                
                // Если это первый канал, возвращаем "последний из установленных" + 1 (а не количество каналов)
                if (currentId === firstId) {
                    return this.getDhLockedMailingsPriority()
                }
                
                // Если это не первый канал, возвращаем null (крестик)
                return null
            }
            
            // Для остальных каналов возвращаем обычный приоритет
            return this.getChannelPriority(item.connectorId || item.id, 'mailings')
        },
        getMailingsPriorityTooltip(item) {
            if (!this.isWhatsappDhChannel(item)) return ''
            
            const firstChannel = this.getFirstWhatsappDhChannel()
            if (!firstChannel) return ''
            
            const currentId = item.connectorId || item.id
            const firstId = firstChannel.connectorId || firstChannel.id
            
            if (currentId === firstId) {
                return 'Это бесплатный канал, приоритет изменить нельзя.'
            } else {
                return 'У вас уже установлен бесплатный канал в качестве последнего приоритета, данный канал используется только для маркетинговых рассылок.'
            }
        },
        getNotificationsPriorityForWhatsappDh(item) {
            // Если это канал WhatsApp с провайдером Data Heroes, всегда возвращаем null (крестик)
            if (this.isWhatsappDhChannel(item)) {
                return null
            }
            
            // Для остальных каналов возвращаем обычный приоритет
            return this.getChannelPriority(item.connectorId || item.id, 'notifications')
        },
        getNotificationsPriorityTooltip(item) {
            if (!this.isWhatsappDhChannel(item)) return ''
            
            return 'Отправка сервисных уведомлений недоступна через бесплатный канал.'
        },
        getCrmMessagesPriorityForWhatsappDh(item) {
            // Для WhatsApp(DH) в колонке "Сообщения из YCLIENTS (коды подтверждения)" приоритет выбрать нельзя
            if (this.isWhatsappDhChannel(item)) {
                return null
            }
            return this.getChannelPriority(item.connectorId || item.id, 'crmMessages')
        },
        getCrmMessagesPriorityTooltip(item) {
            if (!this.isWhatsappDhChannel(item)) return ''
            return 'Отправка кодов подтверждения из YCLIENTS недоступна через бесплатный канал.'
        },
        getExcludedPriorities(currentConnectorId, type) {
            // Получаем список приоритетов, которые уже заняты другими каналами в этом столбце (типе сообщений)
            const activeChannels = this.tableItems.filter(
                item => item.isActive !== false && (item.connectorId || item.id) && this.isChannelType(item)
            )

            const excludedPriorities = []
            activeChannels.forEach(item => {
                const id = item.connectorId || item.id
                // Исключаем текущий канал из проверки
                if (id !== currentConnectorId) {
                    const priority = this.getChannelPriority(id, type)
                    if (priority !== null && priority !== undefined && priority > 0) {
                        excludedPriorities.push(priority)
                    }
                }
            })

            // Для mailings резервируем последний приоритет под WhatsApp(DH), чтобы его нельзя было выбрать вручную
            if (type === 'mailings') {
                const firstDh = this.getFirstWhatsappDhChannel()
                if (firstDh) {
                    const locked = this.getDhLockedMailingsPriority(
                        this.tableItems.filter(item => item.isActive !== false && (item.connectorId || item.id) && this.isChannelType(item))
                    )
                    if (locked > 0) excludedPriorities.push(locked)
                }
            }

            return excludedPriorities
        },
        async normalizeMailingsPrioritiesForDh() {
            try {
                // Только для таблицы каналов
                if (!this.allChannelsCards) return

                const activeChannels = this.tableItems.filter(
                    item => item.isActive !== false && (item.connectorId || item.id) && this.isChannelType(item)
                )
                if (activeChannels.length === 0) return

                const dhChannels = activeChannels.filter(item => this.isWhatsappDhChannel(item))
                if (dhChannels.length === 0) return

                const firstDh = this.getFirstWhatsappDhChannel()
                if (!firstDh) return

                const firstDhId = firstDh.connectorId || firstDh.id

                // Нормализуем только НЕ-DH каналы: 1..max-1 без дублей, сохраняя порядок по текущим приоритетам
                const nonDh = activeChannels.filter(item => !this.isWhatsappDhChannel(item))
                const nonDhWithPriority = nonDh
                    .map(item => ({
                        id: item.connectorId || item.id,
                        priority: this.getChannelPriority(item.connectorId || item.id, 'mailings'),
                    }))
                    .filter(p => p.id && p.priority !== null && p.priority !== undefined)
                    .sort((a, b) => a.priority - b.priority)

                const desired = {}
                nonDhWithPriority.forEach((p, idx) => {
                    desired[p.id] = idx + 1
                })

                // Всегда фиксируем "первый" DH как последний приоритет: (max установленного среди НЕ-DH) + 1 (минимум 2 при наличии НЕ-DH)
                const maxNonDhAfterNormalize = nonDhWithPriority.length
                const hasNonDh = nonDh.length > 0
                const lockedDhPriority = hasNonDh ? Math.max(2, maxNonDhAfterNormalize + 1) : 1
                desired[firstDhId] = lockedDhPriority

                // Остальные DH должны быть null (не участвуют)
                const otherDhIds = dhChannels
                    .map(item => item.connectorId || item.id)
                    .filter(id => id && id !== firstDhId)

                const changes = []

                // Применяем desired для nonDh + firstDh
                Object.keys(desired).forEach((id) => {
                    const next = desired[id]
                    const cur = this.getChannelPriority(id, 'mailings')
                    if (cur !== next) {
                        if (!this.localChannelPriorities[id]) this.$set(this.localChannelPriorities, id, {})
                        this.$set(this.localChannelPriorities[id], 'mailings', next)
                        changes.push({ id, priority: next })
                    }
                })

                // Очистка остальных DH
                otherDhIds.forEach((id) => {
                    const cur = this.getChannelPriority(id, 'mailings')
                    if (cur !== null && cur !== undefined) {
                        if (!this.localChannelPriorities[id]) this.$set(this.localChannelPriorities, id, {})
                        this.$set(this.localChannelPriorities[id], 'mailings', null)
                        changes.push({ id, priority: null })
                    }
                })

                // Если среди nonDh есть приоритет == maxPriority — это конфликт, он уйдет через desired; но если у кого-то нет приоритета,
                // мы его не трогаем.
                if (changes.length === 0) return

                // Сохраняем изменения единым запросом (после нормализации на фронте)
                const normalized = {}
                changes.forEach(({ id, priority }) => {
                    if (priority === null || priority === undefined) return
                    normalized[id] = priority
                })
                await this.saveChannelPrioritiesBatch(normalized, activeChannels, 'mailings', null)
            } catch (e) {
                console.error('normalizeMailingsPrioritiesForDh error:', e)
            }
        },
    },
    async created() {
        // Загружаем app данные для единственного whatsappBasis коннектора (на проекте он может быть только один)
        if (!this.project?.isChannelsRework) {
            const connector = (this.connectors || []).find(
                c => c.connectorType === CONNECTOR_TYPES.whatsappBasis
            )
            if (connector) {
                this.whatsappBasisConnectorId = connector.connectorId
                await this.loadWhatsappBasisMetaAndStatus(connector.connectorId)
                // Запускаем интервальный опрос статуса каждые 10 секунд
                this.whatsappBasisAuthInterval = setInterval(() => {
                    this.loadWhatsappBasisStatus(connector.connectorId)
                }, 10 * 1000)
            }
        }
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'
.inactive-image
    filter: saturate(0%)
::v-deep .inactive-avatar
    background-color: #9E9E9E !important

.connector-row
    display: flex
    align-items: center

.connector-row__name
    flex: 1

.qr-btn
    position: relative

.qr-icon
    font-size: 28px !important

.status-icon
    position: absolute
    top: 1px
    right: 6px
    text-shadow: -1px -1px 0 #FFFFFF, 1px -1px 0 #FFFFFF, -1px 1px 0 #FFFFFF, 1px 1px 0 #FFFFFF

.status-icon--success
    color: #00C853 !important
    font-size: 20px !important
    
.status-icon--error
    right: 7px
    color: red !important
    font-size: 22px !important

// Контейнер для иконки и текста канала (только для вкладки "Каналы")
.channel-cell
    display: flex
    align-items: center

    &--clickable
        cursor: pointer

        &:hover
            opacity: 0.8

// Общие стили таблицы каналов
.channels-table
    background-color: #f9f9fa
    border-radius: 8px
    overflow: hidden

    ::v-deep thead tr
        background-color: #f9f9fa

    ::v-deep tbody tr
        background-color: $app-bcg-new
        cursor: pointer
        &:hover
            background-color: rgba(0, 0, 0, 0.04) !important

// Типографика для названия канала и телефона (только для вкладки "Каналы")
.channel-text
    display: flex
    flex-direction: column

.channel-name
    font-weight: 600
    font-size: 16px
    line-height: 1.3

.channel-name--inactive
    color: #aab1b9

.channel-phone
    font-weight: 400
    font-size: 12px
    line-height: 1.3
    color: #757575

// Колонка статуса авторизации (иконки)
.auth-status-cell
    display: flex
    justify-content: flex-start
    align-items: center

// Если каскадов нет — правую колонку (статусы/кнопка "Добавить") центрируем справа
.channels-table--no-cascades
    .auth-status-cell
        justify-content: flex-end
        padding-right: 12px

.auth-status-wrapper
    display: flex
    align-items: center
    gap: 4px
    padding: 2px 6px

.auth-status-wrapper--error
    border-radius: 2px
    border: 1px solid rgba(226, 0, 41, 0.4)

.auth-status-icon
    width: 18px
    height: 18px

.auth-status-spinner
    margin-right: 2px

.auth-status-icon--ok
    width: 20px
    height: 20px

.auth-status-text
    font-size: 12px
    font-weight: 400
    line-height: 1.3
    white-space: nowrap

.auth-status-text--ok
    color: rgba(42, 59, 81, 0.38)

.auth-status-text--error
    color: #E20029

.auth-status-text--offline
    color: #C7770B

.auth-status-text--online
    color: #2E7D32

// Стили для кнопки "Добавить" - должна быть такой же ширины, как текст "Не подключен"
::v-deep .connect-btn-list
    min-width: 100px !important
    width: 100px !important

// Стили для колонок приоритетов
.priority-header
    display: flex
    align-items: center
    justify-content: center
    flex-wrap: nowrap
    gap: 4px

    span
        line-height: 1.4
        &:first-child
            text-align: center

.hint-icon
    color: rgba(0, 0, 0, 0.38)
    opacity: 0.38
    cursor: pointer
    flex-shrink: 0

.hint-icon:hover
    color: black
    opacity: 1

.priority-cell
    display: flex
    justify-content: center
    align-items: center

// Стили кнопок подключения только для списка каналов
::v-deep .channels-connect-btn--free
    background-color: $main !important
    border-color: #238ee7 !important
    color: #ffffff !important
    span
        color: #ffffff !important

::v-deep .channels-connect-btn--paid
    background-color: #00b74f !important
    border-color: #00b74f!important
    color: #ffffff !important
    span
        color: #ffffff !important
</style>
